const appendWidth = (src, width) => {
  if (!src) return src;
  const joiner = src.includes('?') ? '&' : '?';
  return `${src}${joiner}width=${width}`;
};

const STRIP_OVERFLOW_TOLERANCE = 1;
const PRODUCT_HERO_IMAGE_WIDTH = 1200;
const deepLinkedMediaId = new URLSearchParams(window.location.search).get('vika_media');
const userPickedFieldsetsBySection = new Map();
const preloadedMediaSrcs = new Set();

const getProductThumbnails = (section) =>
  Array.from(section?.querySelectorAll('[data-vika-product-thumbnail]') || []).filter(
    (thumbnail) => thumbnail instanceof HTMLButtonElement
  );

const getRelatedStrip = (section) => section?.querySelector('[data-vika-related-strip]');
const getRelatedStripTrack = (strip) => strip?.querySelector('[data-vika-related-strip-track]');

const preloadMediaSrc = (src) => {
  if (!src || preloadedMediaSrcs.has(src)) return;

  const image = new Image();
  image.decoding = 'async';
  image.src = src;
  preloadedMediaSrcs.add(src);
};

const updateRelatedStripOverflowState = (strip) => {
  if (!(strip instanceof HTMLElement)) return;
  const track = getRelatedStripTrack(strip);
  if (!(track instanceof HTMLElement)) return;

  const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
  const hasOverflow = maxScrollLeft > STRIP_OVERFLOW_TOLERANCE;
  const atStart = track.scrollLeft <= STRIP_OVERFLOW_TOLERANCE;
  const atEnd = track.scrollLeft >= maxScrollLeft - STRIP_OVERFLOW_TOLERANCE;

  strip.classList.toggle('is-scrollable', hasOverflow);
  strip.classList.toggle('has-overflow-start', hasOverflow && !atStart);
  strip.classList.toggle('has-overflow-end', hasOverflow && !atEnd);
};

const ensureThumbnailInView = (section, thumbnail, behavior = 'smooth') => {
  if (!section || !(thumbnail instanceof HTMLButtonElement)) return;

  const strip = getRelatedStrip(section);
  const track = getRelatedStripTrack(strip);
  if (!(track instanceof HTMLElement)) return;
  if (!(strip instanceof HTMLElement)) return;

  const thumbnailItem = thumbnail.closest('li');
  const target = thumbnailItem instanceof HTMLElement ? thumbnailItem : thumbnail;
  target.scrollIntoView({ behavior, block: 'nearest', inline: 'nearest' });
  updateRelatedStripOverflowState(strip);
};

const initRelatedImageStrip = (strip) => {
  if (!(strip instanceof HTMLElement)) return;
  const track = getRelatedStripTrack(strip);
  if (!(track instanceof HTMLElement)) return;

  if (strip.dataset.vikaStripInitialized !== 'true') {
    track.addEventListener(
      'scroll',
      () => {
        updateRelatedStripOverflowState(strip);
      },
      { passive: true }
    );
    strip.dataset.vikaStripInitialized = 'true';
  }

  updateRelatedStripOverflowState(strip);
};

const initRelatedImageStrips = () => {
  document.querySelectorAll('[data-vika-related-strip]').forEach((strip) => {
    initRelatedImageStrip(strip);
  });
};

const applyDeepLinkedMediaSelection = (section) => {
  if (!section || !deepLinkedMediaId) return;
  if (section.dataset.vikaDeepLinkedMediaApplied === 'true') return;

  section.dataset.vikaDeepLinkedMediaApplied = 'true';

  const thumbnail = getProductThumbnails(section).find(
    (candidate) => candidate.dataset.vikaMediaId === String(deepLinkedMediaId)
  );

  if (thumbnail instanceof HTMLButtonElement) {
    applyThumbnailSelection(section, thumbnail, 'auto');
  }
};

const preloadProductMediaSources = (section) => {
  if (!(section instanceof HTMLElement)) return;

  const thumbnails = getProductThumbnails(section);
  if (!thumbnails.length) return;

  const activeIndex = getActiveThumbnailIndex(thumbnails);
  const baseIndex = activeIndex >= 0 ? activeIndex : 0;
  const eagerIndices = [baseIndex];

  if (thumbnails.length > 1) {
    eagerIndices.push((baseIndex + 1) % thumbnails.length);
  }

  eagerIndices.forEach((index) => {
    const src = thumbnails[index]?.dataset.vikaMediaSrc;
    if (!src) return;
    preloadMediaSrc(src);
  });
};

const initProductSection = (section) => {
  if (!(section instanceof HTMLElement)) return;
  if (!section.querySelector('[data-vika-product-media-image]')) return;

  const strip = getRelatedStrip(section);
  if (strip instanceof HTMLElement) {
    initRelatedImageStrip(strip);
  }

  applyDeepLinkedMediaSelection(section);
  applyUserPickedFieldsetState(section);
  preloadProductMediaSources(section);
};

const initProductSections = () => {
  document.querySelectorAll('.shopify-section').forEach((section) => {
    initProductSection(section);
  });
};

const getSectionKey = (section) => {
  if (!(section instanceof HTMLElement)) return null;
  const explicitId = section.dataset.sectionId;
  if (typeof explicitId === 'string' && explicitId.length > 0) return explicitId;
  if (typeof section.id === 'string' && section.id.length > 0) return section.id;
  return null;
};

const getUserPickedFieldsetSet = (section) => {
  const sectionKey = getSectionKey(section);
  if (!sectionKey) return null;
  let fieldsetSet = userPickedFieldsetsBySection.get(sectionKey);
  if (!(fieldsetSet instanceof Set)) {
    fieldsetSet = new Set();
    userPickedFieldsetsBySection.set(sectionKey, fieldsetSet);
  }
  return fieldsetSet;
};

const markUserPickedFieldset = (section, fieldsetIndex) => {
  if (!(section instanceof HTMLElement)) return;
  if (fieldsetIndex == null || fieldsetIndex === '') return;
  const normalizedIndex = String(fieldsetIndex);
  const fieldsetSet = getUserPickedFieldsetSet(section);
  if (!(fieldsetSet instanceof Set)) return;
  fieldsetSet.add(normalizedIndex);
  applyUserPickedFieldsetState(section);
};

const applyUserPickedFieldsetState = (section) => {
  if (!(section instanceof HTMLElement)) return;
  const fieldsetSet = getUserPickedFieldsetSet(section);
  if (!(fieldsetSet instanceof Set)) return;

  section.querySelectorAll('.vika-product-variants fieldset[data-fieldset-index]').forEach((fieldset) => {
    if (!(fieldset instanceof HTMLElement)) return;
    const fieldsetIndex = fieldset.dataset.fieldsetIndex;
    if (fieldsetIndex && fieldsetSet.has(String(fieldsetIndex))) {
      fieldset.dataset.vikaUserPicked = 'true';
    } else {
      delete fieldset.dataset.vikaUserPicked;
    }
  });
};

const hasUserPickedVariantOption = (section) => {
  const fieldsetSet = getUserPickedFieldsetSet(section);
  return fieldsetSet instanceof Set && fieldsetSet.size > 0;
};

const syncActiveThumbnail = (section, mediaId) => {
  if (!section || !mediaId) return;

  let activeThumbnail = null;
  const thumbnails = getProductThumbnails(section);
  thumbnails.forEach((thumbnail) => {
    const isActive = thumbnail.dataset.vikaMediaId === String(mediaId);
    if (isActive) {
      thumbnail.setAttribute('aria-current', 'true');
      thumbnail.classList.add('is-active');
      activeThumbnail = thumbnail;
    } else {
      thumbnail.removeAttribute('aria-current');
      thumbnail.classList.remove('is-active');
    }
  });

  return activeThumbnail;
};

const applyThumbnailSelection = (section, thumbnail, behavior = 'smooth') => {
  if (!section || !(thumbnail instanceof HTMLButtonElement)) return;

  const image = section.querySelector('[data-vika-product-media-image]');
  if (!(image instanceof HTMLImageElement)) return;

  const nextSrc = thumbnail.dataset.vikaMediaSrc;
  if (!nextSrc) return;

  image.src = nextSrc;
  image.removeAttribute('srcset');
  image.dataset.vikaDefaultImage = nextSrc;

  const nextAlt = thumbnail.dataset.vikaMediaAlt;
  if (typeof nextAlt === 'string' && nextAlt.length > 0) {
    image.alt = nextAlt;
  }

  syncActiveThumbnail(section, thumbnail.dataset.vikaMediaId);
  ensureThumbnailInView(section, thumbnail, behavior);
  preloadProductMediaSources(section);
};

const getActiveThumbnailIndex = (thumbnails) => {
  if (!thumbnails.length) return -1;
  const activeIndex = thumbnails.findIndex((thumbnail) => thumbnail.classList.contains('is-active'));
  if (activeIndex >= 0) return activeIndex;
  return thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-current') === 'true');
};

const cycleThumbnail = (section, direction) => {
  if (!section) return;

  const thumbnails = getProductThumbnails(section);
  if (!thumbnails.length) return;

  const currentIndex = getActiveThumbnailIndex(thumbnails);
  const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
  const nextIndex = (safeCurrentIndex + direction + thumbnails.length) % thumbnails.length;
  const nextThumbnail = thumbnails[nextIndex];
  if (!(nextThumbnail instanceof HTMLButtonElement)) return;

  applyThumbnailSelection(section, nextThumbnail, 'auto');
};

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const prevButton = target.closest('[data-vika-image-prev]');
  if (prevButton instanceof HTMLButtonElement) {
    const section = prevButton.closest('.shopify-section');
    cycleThumbnail(section, -1);
    return;
  }

  const nextButton = target.closest('[data-vika-image-next]');
  if (nextButton instanceof HTMLButtonElement) {
    const section = nextButton.closest('.shopify-section');
    cycleThumbnail(section, 1);
    return;
  }

  const thumbnail = target.closest('[data-vika-product-thumbnail]');
  if (!(thumbnail instanceof HTMLButtonElement)) return;

  const section = thumbnail.closest('.shopify-section');
  applyThumbnailSelection(section, thumbnail);
});

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.type !== 'radio') return;
  if (!target.closest('.vika-product-variants')) return;

  const section = target.closest('.shopify-section');
  if (!(section instanceof HTMLElement)) return;
  const fieldset = target.closest('fieldset[data-fieldset-index]');
  if (!(fieldset instanceof HTMLElement)) return;

  markUserPickedFieldset(section, fieldset.dataset.fieldsetIndex);
});

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const radio = target.closest('.vika-product-variants input[type="radio"]');
  if (!(radio instanceof HTMLInputElement)) return;

  const section = radio.closest('.shopify-section');
  if (!(section instanceof HTMLElement)) return;
  const fieldset = radio.closest('fieldset[data-fieldset-index]');
  if (!(fieldset instanceof HTMLElement)) return;

  markUserPickedFieldset(section, fieldset.dataset.fieldsetIndex);
});

document.addEventListener('variant:update', (event) => {
  const section = event.target.closest('.shopify-section');
  if (!section) return;

  if (!hasUserPickedVariantOption(section)) {
    initProductSection(section);
    return;
  }

  const image = section.querySelector('[data-vika-product-media-image]');
  if (!(image instanceof HTMLImageElement)) return;

  const previewSrc = event.detail?.resource?.featured_media?.preview_image?.src;
  const featuredMediaId = event.detail?.resource?.featured_media?.id;
  const nextSrc = previewSrc ? appendWidth(previewSrc, PRODUCT_HERO_IMAGE_WIDTH) : null;
  if (!nextSrc) {
    initProductSection(section);
    return;
  }

  image.src = nextSrc;
  image.removeAttribute('srcset');
  image.dataset.vikaDefaultImage = nextSrc;

  if (featuredMediaId) {
    const activeThumbnail = syncActiveThumbnail(section, featuredMediaId);
    if (activeThumbnail instanceof HTMLButtonElement) {
      ensureThumbnailInView(section, activeThumbnail);
    }
  }

  initProductSection(section);
});

window.addEventListener('resize', initRelatedImageStrips);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initRelatedImageStrips();
    initProductSections();
  });
} else {
  initRelatedImageStrips();
  initProductSections();
}
